import React, { createContext } from 'react';
import { useTranslation } from 'react-i18next';
import DraftConflictModal from '@components/atoms/modals/draft-conflict-modal.tsx';
import { openCloudVersionInNewTab } from '@utils/localStorage/draft-conflict.ts';
import type {
  CaptionContextType,
  CaptionProviderProps
} from './caption-context-state';
import { useCaptionContextLogic } from './use-caption-context-logic';

export type { CaptionContextType };

export const CaptionContext = createContext<CaptionContextType | undefined>(
  undefined
);

export function CaptionProvider({
  children,
  session,
  document
}: CaptionProviderProps) {
  const { t } = useTranslation();
  const {
    contextValue,
    conflict,
    resolveConflictKeepLocal,
    resolveConflictLoadCloud
  } = useCaptionContextLogic({ session, document });

  return (
    <CaptionContext.Provider value={contextValue}>
      {conflict ? (
        <DraftConflictModal
          isOpen
          localSavedAt={conflict.localTimestamp}
          serverSavedAt={conflict.serverTimestamp}
          localCount={conflict.localCount}
          serverCount={conflict.serverCount}
          unit={t('caption-cues-unit', 'cues')}
          onKeepLocal={resolveConflictKeepLocal}
          onLoadCloud={resolveConflictLoadCloud}
          onViewCloud={openCloudVersionInNewTab}
        />
      ) : (
        children
      )}
    </CaptionContext.Provider>
  );
}

export function useCaption() {
  const context = React.useContext(CaptionContext);
  if (context === undefined) {
    throw new Error('useCaption must be used within a CaptionProvider');
  }
  return context;
}
